/**
 * ffmpeg `-filter_complex` 文字列を組み立てる純関数群。
 *
 * 設計方針:
 *   - 副作用ゼロ (ファイル I/O なし)。単体テストで境界値を完全検証する。
 *   - 80 枚超でもチェーンが壊れないよう、xfade の offset を累積で正確計算。
 *   - ロゴはオプション (undefined なら overlay なし、最終ラベルをそのまま返す)。
 *
 * xfade の offset 算出:
 *   offset は「トランジション開始時刻 (前クリップの先頭からの秒数)」。
 *   例) clip[0]=4秒, clip[1]=2秒, crossfade=0.3:
 *     [v0][v1]xfade=duration=0.3:offset=3.7[x1]  // 3.7 秒で fade 開始
 *     x1 の出力長 = 4 + 2 - 0.3 = 5.7 秒
 *   次の xfade:
 *     [x1][v2]xfade=duration=0.3:offset=5.4[x2]  // 5.7 - 0.3 = 5.4
 *     x2 の出力長 = 5.7 + 2 - 0.3 = 7.4 秒
 *
 * 参考: https://ffmpeg.org/ffmpeg-filters.html#xfade
 */

import { VIDEO_SPEC } from './types';

export interface ClipSpec {
  readonly durationSec: number;
}

export interface FilterGraphConfig {
  readonly clips: readonly ClipSpec[];
  readonly crossfadeSec: number;
  readonly outputWidth: number;
  readonly outputHeight: number;
  /** ロゴの ffmpeg 入力インデックス (=clips.length)。undefined ならロゴなし */
  readonly logoInputIndex?: number;
  readonly logoWidthPx?: number;
  readonly logoMarginPx?: number;
}

export interface FilterGraphResult {
  /** -filter_complex に渡す文字列 */
  readonly complexFilter: string;
  /** -map に渡すラベル (例: "[out]") */
  readonly finalLabel: string;
  /** 動画の総秒数 (クロスフェードで重なる分を差し引き済み) */
  readonly totalDurationSec: number;
  /** 各 xfade の offset (デバッグ用) */
  readonly xfadeOffsets: readonly number[];
}

/**
 * 入力インデックス配列 + クロスフェード + (任意) ロゴ重ね を組み立てる。
 *
 * @throws RangeError - clips が空 / crossfadeSec がクリップより長い等
 */
export function buildFilterGraph(config: FilterGraphConfig): FilterGraphResult {
  validateConfig(config);

  const lines: string[] = [];

  // 1. 各入力を scale + pad + setsar で 4:3 1920×1440 (背景白パディング) に揃える
  for (let i = 0; i < config.clips.length; i++) {
    lines.push(buildScalePadLine(i, config.outputWidth, config.outputHeight));
  }

  // 2. xfade チェーン
  const xfade = buildXfadeChain(config.clips, config.crossfadeSec);
  lines.push(...xfade.lines);

  // 3. ロゴ overlay (任意)
  const finalLabel = xfade.lastLabel;
  let outLabel = finalLabel;
  if (
    config.logoInputIndex !== undefined &&
    config.logoWidthPx !== undefined &&
    config.logoMarginPx !== undefined
  ) {
    const logoLabel = `[logo]`;
    // ロゴを指定幅にリサイズ (高さはアスペクト比保持 -1)
    lines.push(`[${config.logoInputIndex}:v]scale=${config.logoWidthPx}:-1${logoLabel}`);
    // 右下から margin だけ離して overlay
    lines.push(
      `${finalLabel}${logoLabel}overlay=main_w-overlay_w-${config.logoMarginPx}:main_h-overlay_h-${config.logoMarginPx}[out]`,
    );
    outLabel = `[out]`;
  }

  return {
    complexFilter: lines.join(';'),
    finalLabel: outLabel,
    totalDurationSec: xfade.totalDurationSec,
    xfadeOffsets: xfade.offsets,
  };
}

// ===== 内部ヘルパ =======================================================

function validateConfig(config: FilterGraphConfig): void {
  if (config.clips.length === 0) {
    throw new RangeError('clips must have at least 1 entry');
  }
  if (config.crossfadeSec < 0) {
    throw new RangeError(`crossfadeSec must be >= 0, got ${config.crossfadeSec}`);
  }
  for (let i = 0; i < config.clips.length; i++) {
    const c = config.clips[i];
    if (!c || c.durationSec <= 0) {
      throw new RangeError(`clip[${i}].durationSec must be > 0`);
    }
    // xfade は両クリップが crossfade 長以上ないと破綻する。先頭以外で検査。
    if (i > 0 && c.durationSec <= config.crossfadeSec) {
      throw new RangeError(
        `clip[${i}].durationSec (${c.durationSec}) must be > crossfadeSec (${config.crossfadeSec})`,
      );
    }
  }
  if (config.outputWidth <= 0 || config.outputHeight <= 0) {
    throw new RangeError('outputWidth/outputHeight must be positive');
  }
}

function buildScalePadLine(idx: number, w: number, h: number): string {
  // 入力画像の比率を保ったまま 1920×1440 フレーム内に収める。
  // 既に 4:3 (2560×1920 等) の場合は pad が noop に近い。
  // 比率が違う場合 (万一 16:9 JPEG が混入等) は上下/左右に白パディング。
  return (
    `[${idx}:v]scale=${w}:${h}:force_original_aspect_ratio=decrease,` +
    `pad=${w}:${h}:(ow-iw)/2:(oh-ih)/2:color=white,setsar=1,format=yuv420p[v${idx}]`
  );
}

function buildXfadeChain(
  clips: readonly ClipSpec[],
  crossfadeSec: number,
): {
  readonly lines: readonly string[];
  readonly lastLabel: string;
  readonly totalDurationSec: number;
  readonly offsets: readonly number[];
} {
  if (clips.length === 1) {
    // xfade 不要。[v0] をそのまま最終ラベル扱い
    return {
      lines: [],
      lastLabel: '[v0]',
      totalDurationSec: clips[0]?.durationSec ?? 0,
      offsets: [],
    };
  }

  const lines: string[] = [];
  const offsets: number[] = [];
  // 初期状態: [v0] の累積長さ = clips[0].duration
  let cumulativeDuration = clips[0]?.durationSec ?? 0;
  let prevLabel = '[v0]';

  for (let i = 1; i < clips.length; i++) {
    const clip = clips[i];
    if (!clip) throw new Error(`clip[${i}] missing`);
    // offset = 現在の累積長 - crossfadeSec (つまりトランジション開始点)
    const offset = roundTo3(cumulativeDuration - crossfadeSec);
    offsets.push(offset);
    const nextLabel = i === clips.length - 1 ? '[xfinal]' : `[x${i}]`;
    lines.push(
      `${prevLabel}[v${i}]xfade=transition=fade:duration=${crossfadeSec}:offset=${offset}${nextLabel}`,
    );
    // 累積長の更新: xfade 後は「前の累積 + 新クリップ - crossfade」
    cumulativeDuration = roundTo3(cumulativeDuration + clip.durationSec - crossfadeSec);
    prevLabel = nextLabel;
  }

  return {
    lines,
    lastLabel: prevLabel,
    totalDurationSec: cumulativeDuration,
    offsets,
  };
}

function roundTo3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

// ===== 呼び出し側ヘルパ =================================================

/**
 * VIDEO_SPEC を使ったデフォルト設定でフィルタグラフを構築する簡易関数。
 */
export function buildDefaultFilterGraph(
  clips: readonly ClipSpec[],
  hasLogo: boolean,
): FilterGraphResult {
  const logoWidthPx = Math.round(VIDEO_SPEC.WIDTH * VIDEO_SPEC.LOGO_WIDTH_RATIO);
  const baseConfig: FilterGraphConfig = {
    clips,
    crossfadeSec: VIDEO_SPEC.CROSSFADE_SEC,
    outputWidth: VIDEO_SPEC.WIDTH,
    outputHeight: VIDEO_SPEC.HEIGHT,
  };
  if (hasLogo) {
    return buildFilterGraph({
      ...baseConfig,
      logoInputIndex: clips.length,
      logoWidthPx,
      logoMarginPx: VIDEO_SPEC.LOGO_MARGIN_PX,
    });
  }
  return buildFilterGraph(baseConfig);
}
